import React, { useCallback, useEffect, useState } from 'react';
import { UserPlus, RefreshCw, Shield, Edit2, Trash2, X, KeyRound } from 'lucide-react';
import apiClient from '../../utils/apiClient';
import { useAuth } from '../../context/AuthContext';

const ROLE_STYLES = {
  platform_admin: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  platform_support: 'bg-blue-50 text-blue-700 border-blue-200',
};

const BLANK_CREATE = {
  full_name: '',
  email: '',
  password: '',
  platform_role: 'platform_support',
};

const fmtDate = (d) => (d ? new Date(d).toLocaleDateString() : '—');

const PlatformUsersPanel = () => {
  const { user, isPlatformAdmin } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [createModal, setCreateModal] = useState(null);
  const [editModal, setEditModal] = useState(null);
  const [working, setWorking] = useState(false);

  const fetchUsers = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await apiClient.get('/api/platform/users');
      // The API serializes the id under its Pydantic alias `_id`
      // (UserResponse uses Field(alias="_id")). Normalize to `id` so
      // edit/delete/isSelf don't send "undefined" -> "Invalid user id".
      setUsers(
        (res.data || []).map((u) => ({ ...u, id: u.id || u._id })),
      );
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to load platform users');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchUsers(); }, [fetchUsers]);

  const submitCreate = async (e) => {
    e.preventDefault();
    setWorking(true);
    setError(null);
    try {
      await apiClient.post('/api/platform/users', {
        email: createModal.email.trim().toLowerCase(),
        full_name: createModal.full_name.trim(),
        password: createModal.password,
        platform_role: createModal.platform_role,
      });
      setCreateModal(null);
      await fetchUsers();
    } catch (err) {
      setError(err.response?.data?.detail || 'Could not create user');
    } finally {
      setWorking(false);
    }
  };

  const submitEdit = async (e) => {
    e.preventDefault();
    setWorking(true);
    setError(null);
    const payload = {
      full_name: editModal.full_name,
      platform_role: editModal.platform_role,
      is_active: editModal.is_active,
    };
    if (editModal.password) payload.password = editModal.password;
    try {
      await apiClient.put(`/api/platform/users/${editModal.id}`, payload);
      setEditModal(null);
      await fetchUsers();
    } catch (err) {
      setError(err.response?.data?.detail || 'Update failed');
    } finally {
      setWorking(false);
    }
  };

  const handleDelete = async (u) => {
    if (!window.confirm(`Delete ${u.email}? This cannot be undone.`)) return;
    setWorking(true);
    try {
      await apiClient.delete(`/api/platform/users/${u.id}`);
      await fetchUsers();
    } catch (err) {
      setError(err.response?.data?.detail || 'Delete failed');
    } finally {
      setWorking(false);
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-[15px] font-semibold text-slate-900 dark:text-slate-100">Platform users</h2>
          <p className="text-[12px] text-slate-500 dark:text-slate-400">Vendor staff with cross-tenant access. Admin-only management.</p>
        </div>
        <div className="flex items-center gap-2">
          {isPlatformAdmin() && (
            <button
              onClick={() => setCreateModal(BLANK_CREATE)}
              className="flex items-center gap-1.5 px-3 py-1.5 text-[13px] font-medium text-white bg-emerald-600 hover:bg-emerald-700 rounded-lg"
            >
              <UserPlus className="h-3.5 w-3.5" />
              New user
            </button>
          )}
          <button
            onClick={fetchUsers}
            disabled={loading}
            className="flex items-center gap-1.5 px-3 py-1.5 text-[13px] text-slate-600 dark:text-slate-300 hover:text-slate-900 dark:hover:text-white hover:bg-slate-50 dark:hover:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg disabled:opacity-50"
          >
            <RefreshCw className={`h-3.5 w-3.5 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </button>
        </div>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 text-[13px] rounded-lg px-4 py-3 flex items-start justify-between gap-3">
          <span>{error}</span>
          <button onClick={() => setError(null)} className="text-red-500 hover:text-red-700">
            <X className="h-4 w-4" />
          </button>
        </div>
      )}

      <div className="bg-white dark:bg-slate-900 rounded-xl border border-slate-200/80 dark:border-slate-800 overflow-hidden">
        {loading ? (
          <div className="p-8 text-center text-[13px] text-slate-500">Loading…</div>
        ) : users.length === 0 ? (
          <div className="p-8 text-center text-[13px] text-slate-500">
            <Shield className="h-8 w-8 text-slate-300 mx-auto mb-2" />
            No platform users yet.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-[13px]">
              <thead className="bg-slate-50 dark:bg-slate-800/50 text-[11px] uppercase tracking-wider text-slate-500 dark:text-slate-400">
                <tr>
                  <th className="px-4 py-2 text-left font-semibold">Name</th>
                  <th className="px-4 py-2 text-left font-semibold">Email</th>
                  <th className="px-4 py-2 text-left font-semibold">Role</th>
                  <th className="px-4 py-2 text-left font-semibold">Active</th>
                  <th className="px-4 py-2 text-left font-semibold">Created</th>
                  <th className="px-4 py-2 text-right font-semibold">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
                {users.map((u) => {
                  const isSelf = u.id === user?.id;
                  const roleStyle = ROLE_STYLES[u.platform_role] || 'bg-slate-100 text-slate-500 border-slate-200';
                  return (
                    <tr key={u.id} className="hover:bg-slate-50/60 dark:hover:bg-slate-800/40">
                      <td className="px-4 py-2.5 font-medium text-slate-900 dark:text-slate-100">
                        {u.full_name}
                        {isSelf && <span className="ml-1 text-[10px] text-slate-400 dark:text-slate-500">(you)</span>}
                      </td>
                      <td className="px-4 py-2.5 text-slate-600 dark:text-slate-400">{u.email}</td>
                      <td className="px-4 py-2.5">
                        <span className={`inline-block px-1.5 py-0.5 text-[10px] rounded font-medium border ${roleStyle}`}>
                          {u.platform_role}
                        </span>
                      </td>
                      <td className="px-4 py-2.5">
                        <span
                          className={`inline-block px-1.5 py-0.5 text-[10px] rounded font-medium ${
                            u.is_active ? 'bg-emerald-50 text-emerald-700' : 'bg-slate-100 text-slate-500'
                          }`}
                        >
                          {u.is_active ? 'Yes' : 'No'}
                        </span>
                      </td>
                      <td className="px-4 py-2.5 text-slate-500 dark:text-slate-400 text-[11px]">{fmtDate(u.created_at)}</td>
                      <td className="px-4 py-2.5 text-right">
                        {isPlatformAdmin() && (
                          <div className="flex items-center justify-end gap-1">
                            <button
                              onClick={() =>
                                setEditModal({
                                  id: u.id,
                                  email: u.email,
                                  full_name: u.full_name,
                                  platform_role: u.platform_role,
                                  is_active: u.is_active,
                                  password: '',
                                  isSelf,
                                })
                              }
                              className="p-1.5 text-slate-500 hover:text-slate-900 hover:bg-slate-100 rounded-md"
                              title="Edit"
                            >
                              <Edit2 className="h-3.5 w-3.5" />
                            </button>
                            {!isSelf && (
                              <button
                                onClick={() => handleDelete(u)}
                                disabled={working}
                                className="p-1.5 text-rose-500 hover:text-rose-700 hover:bg-rose-50 rounded-md disabled:opacity-40"
                                title="Delete"
                              >
                                <Trash2 className="h-3.5 w-3.5" />
                              </button>
                            )}
                          </div>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {createModal && (
        <Modal title="New platform user" onClose={() => setCreateModal(null)}>
          <form onSubmit={submitCreate} className="space-y-3">
            <Field label="Full name">
              <input
                type="text"
                required
                value={createModal.full_name}
                onChange={(e) => setCreateModal({ ...createModal, full_name: e.target.value })}
                className="input"
              />
            </Field>
            <Field label="Email">
              <input
                type="email"
                required
                value={createModal.email}
                onChange={(e) => setCreateModal({ ...createModal, email: e.target.value })}
                className="input"
              />
            </Field>
            <Field label="Temporary password" hint="At least 8 characters. User should change on first login.">
              <input
                type="text"
                required
                minLength={8}
                value={createModal.password}
                onChange={(e) => setCreateModal({ ...createModal, password: e.target.value })}
                className="input"
              />
            </Field>
            <Field label="Platform role">
              <select
                value={createModal.platform_role}
                onChange={(e) => setCreateModal({ ...createModal, platform_role: e.target.value })}
                className="input"
              >
                <option value="platform_support">platform_support — full cross-tenant access (cannot create platform users)</option>
                <option value="platform_admin">platform_admin — full cross-tenant + manages platform users</option>
              </select>
            </Field>
            <FooterButtons onClose={() => setCreateModal(null)} working={working} submitLabel="Create" />
          </form>
        </Modal>
      )}

      {editModal && (
        <Modal title={`Edit — ${editModal.email}`} onClose={() => setEditModal(null)}>
          <form onSubmit={submitEdit} className="space-y-3">
            <Field label="Full name">
              <input
                type="text"
                required
                value={editModal.full_name}
                onChange={(e) => setEditModal({ ...editModal, full_name: e.target.value })}
                className="input"
              />
            </Field>
            <Field label="Platform role">
              <select
                value={editModal.platform_role}
                onChange={(e) => setEditModal({ ...editModal, platform_role: e.target.value })}
                disabled={editModal.isSelf}
                className="input disabled:bg-slate-50 disabled:text-slate-400"
              >
                <option value="platform_support">platform_support</option>
                <option value="platform_admin">platform_admin</option>
              </select>
              {editModal.isSelf && <p className="text-[11px] text-slate-400 mt-1">You can't demote yourself.</p>}
            </Field>
            <Field label="Active">
              <label className="flex items-center gap-2 text-[13px] text-slate-700">
                <input
                  type="checkbox"
                  checked={editModal.is_active}
                  onChange={(e) => setEditModal({ ...editModal, is_active: e.target.checked })}
                  disabled={editModal.isSelf}
                />
                User can log in
              </label>
              {editModal.isSelf && <p className="text-[11px] text-slate-400 mt-1">You can't deactivate yourself.</p>}
            </Field>
            <Field label="Reset password" hint="Leave blank to keep current password.">
              <div className="relative">
                <KeyRound className="h-3.5 w-3.5 absolute left-2.5 top-2.5 text-slate-400" />
                <input
                  type="text"
                  value={editModal.password}
                  onChange={(e) => setEditModal({ ...editModal, password: e.target.value })}
                  placeholder="(unchanged)"
                  className="input pl-8"
                />
              </div>
            </Field>
            <FooterButtons onClose={() => setEditModal(null)} working={working} submitLabel="Save" />
          </form>
        </Modal>
      )}

      <style>{`
        .input {
          width: 100%;
          padding: 8px 12px;
          font-size: 13px;
          border: 1px solid #e2e8f0;
          border-radius: 8px;
          outline: none;
        }
        .input:focus {
          border-color: #10b981;
          box-shadow: 0 0 0 3px rgb(16 185 129 / 0.15);
        }
      `}</style>
    </div>
  );
};

const Field = ({ label, hint, children }) => (
  <div>
    <label className="block text-[12px] font-semibold text-slate-600 mb-1">{label}</label>
    {children}
    {hint && <p className="text-[11px] text-slate-400 mt-1">{hint}</p>}
  </div>
);

const Modal = ({ title, onClose, children }) => (
  <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/40 dark:bg-slate-950/60 backdrop-blur-sm px-4">
    <div className="bg-white dark:bg-slate-900 rounded-xl shadow-xl border border-slate-200 dark:border-slate-800 w-full max-w-md">
      <div className="flex items-center justify-between px-5 py-3 border-b border-slate-200 dark:border-slate-800">
        <h3 className="text-[15px] font-semibold text-slate-900 dark:text-slate-100">{title}</h3>
        <button onClick={onClose} className="p-1 rounded-md text-slate-400 hover:text-slate-700 dark:hover:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-800">
          <X className="h-4 w-4" />
        </button>
      </div>
      <div className="px-5 py-4">{children}</div>
    </div>
  </div>
);

const FooterButtons = ({ onClose, working, submitLabel }) => (
  <div className="flex items-center justify-end gap-2 pt-2">
    <button type="button" onClick={onClose} className="px-3 py-1.5 text-[13px] text-slate-600 hover:bg-slate-50 rounded-lg">
      Cancel
    </button>
    <button
      type="submit"
      disabled={working}
      className="px-3 py-1.5 text-[13px] font-medium text-white bg-emerald-600 hover:bg-emerald-700 rounded-lg disabled:opacity-50"
    >
      {working ? 'Saving…' : submitLabel}
    </button>
  </div>
);

export default PlatformUsersPanel;
